import type {PublishingOption} from '@Components/ai-chat-window/components/publishing-options/publishing-options.types';
import type {LoadingStates} from '@Utils/loading.util';
import type {PromptBoxCoordinatorActions} from '@Components/ai-chat-window/hooks/useAiChatPromptBoxCoordinator';
import type {HistoryItem} from '@AiChat/ai-chat.types';
import type {ReactNode} from 'react';
import type {ActionTileStates} from '@Components/ai-chat-window/components/assistant-message-image-part-action-tiles/assistant-message-image-part-action-tiles.types';
import type {RecommendedTemplate} from '@Components/ai-chat-window/components/assistant-message-image-part-action-tiles/components/templates-for-inspiration-tile/templates-for-inspiration-tile.types';
import type {RecommendedTemplatesData} from '@/ai-chat/ai-chat.types';
import type {AnimationCreationState} from '@Components/ai-chat-window/components/assistant-message-image-part-animation-creations/assistant-message-image-part-animation-creations.types';

type ChatId = string;
type ImageHashedId = string;
type AnimationHashedId = string;

export interface AnimationActionsLoading {
  downloadLoadingState: LoadingStates;
  openEditorLoadingState: LoadingStates;
  loadingPublishingOption: PublishingOption | null;
  publishingLoadingState: LoadingStates;
}

export type AnimationActionsLoadingStatesMap = Record<ChatId, Record<AnimationHashedId, AnimationActionsLoading>>;

export enum AiAgentMode {
  CHAT = 'chat',
  AGENT = 'agent',
}

export interface GeneratedImagePartActionsLoading {
  downloadLoadingState: LoadingStates;
  openEditorLoadingState: LoadingStates;
  shareLoadingState: LoadingStates;
  saveToMyDesignsLoadingState: LoadingStates;
  loadingPublishingOption: PublishingOption | null;
  publishingLoadingState: LoadingStates;
}

export type GeneratedImagePartActionsLoadingStatesMap = Record<ChatId, Record<ImageHashedId, GeneratedImagePartActionsLoading>>;
export type RecommendedTemplatesMap = Record<ChatId, RecommendedTemplatesData>;
export type AnimationCreationStateMap = Record<ChatId, Record<ImageHashedId, AnimationCreationState>>;

export interface AiChatContextType {
  state: {
    hasMessages: boolean;
    currentChatId?: string;
    isSidebarExpanded: boolean;
    chatHistory: HistoryItem[];
    recentChatHistory: HistoryItem[];
    isSmall: boolean;
    isPromptBoxFocused: boolean;
    hasMoreHistory: boolean;
    searchQuery: string | undefined;
    shouldFocusSearch: boolean;
    credits: number;
    isUserLoggedIn: boolean;
    getLoadingStatesForImageActions: (chatId: string, imageHashedId: string) => GeneratedImagePartActionsLoading;
    getLoadingStatesForAnimationActions: (chatId: string, animationHashedId: string) => AnimationActionsLoading;
    getActionTileStates: () => ActionTileStates;
    getRecommendedTemplates: (chatId: string) => RecommendedTemplate[];
    getTemplateSearchTerm: (chatId: string) => string | null | undefined;
    getAnimationCreationState: (chatId: string, imageHashedId: string) => AnimationCreationState;
    isSuperUser: boolean;
    mode: AiAgentMode;
    showDeletedMessages: boolean;
    editingUserMessageIdMap: Record<string, string | null>;
    isPaygUserWithAiChatAccess: boolean;
    isReturningUser: boolean;
    areActionTilesHidden: boolean;
    hasSeenTemplatesTile: boolean;
    isTemplateRedesignFeatureEnabled: boolean;
    isAiChatCreateAVideoFeatureEnabled: boolean;
    landingTipRefreshKey: number;
    isQuickTipsView: boolean;
    isChatHistorySearchLoading: boolean;
  };
  actions: {
    setCurrentChatId: (chatId: string | undefined) => void;
    setCurrentChatIdInternal: (chatId: string | undefined) => void;
    setIsSidebarExpanded: (expanded: boolean) => void;
    setSearchQuery: (query: string | undefined) => void;
    setShouldFocusSearch: (value: boolean) => void;
    setIsPromptBoxFocused: (focused: boolean) => void;
    refetchChatHistory: () => Promise<void>;
    fetchChatHistory: (searchQuery: string | undefined, offset: number, limit: number) => Promise<HistoryItem[]>;
    refreshCredits: () => void;
    onLoginSuccessActions: () => void;
    updateLoadingStatesForImageActions: (chatId: string, imageHashedId: string, updatedStates: Partial<GeneratedImagePartActionsLoading>) => void;
    updateLoadingStatesForAnimationActions: (chatId: string, animationHashedId: string, updatedStates: Partial<AnimationActionsLoading>) => void;
    updateActionTileStates: (updatedStates: Partial<ActionTileStates>) => void;
    updateAnimationCreationState: (chatId: string, imageHashedId: string, updatedState: Partial<AnimationCreationState>) => void;
    updateChatHistoryItem: (chatId: string, updates: Partial<HistoryItem>) => void;
    disconnectChatStream: (chatId: string) => void;
    registerDisconnectChatStream: (disconnectFn: (chatId: string) => void) => void;
    deleteChatHistoryItem: (chatId: string) => void;
    setIsPaygUserWithAiChatAccess: (value: boolean) => void;
    setEditingUserMessageId: (messageId: string | null, chatId?: string) => void;
    setShowDeletedMessages: (show: boolean) => void;
    activateForNewChat: () => void;
    activatePromptBox: () => void;
    registerPromptBoxCoordinator: (actions: PromptBoxCoordinatorActions | null) => void;
    navigateToQuickTips: () => void;
    navigateBack: () => void;
    hideActionTilesPermanently: () => void;
    setSearchTagsForChat: (chatId: string, tags: string[]) => void;
    markTemplatesTileAsSeen: () => void;
  };
}

export interface AiChatProviderProps {
  children: ReactNode;
  credits: number;
  isPaygUserWithAiChatAccess: boolean;
  isReturningUser: boolean;
  isSuperUser: boolean;
  isAiChatCreateAVideoFeatureEnabled: boolean;
  isTemplateRedesignFeatureEnabled: boolean;
  mode?: AiAgentMode;
}

export const AI_CHAT_SIDEBAR_EXPANDED_STATE_COOKIE = 'pmwacsec';
